import { initializeApp } from 'firebase/app'
import { getAnalytics, isSupported } from 'firebase/analytics'
import { getAuth, GoogleAuthProvider, connectAuthEmulator } from 'firebase/auth'
import { debug } from './utils/debug'

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
}

// Initialize Firebase
const app = initializeApp(firebaseConfig)

// Initialize Firebase Auth
const auth = getAuth(app)

// Initialize Firebase Functions
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions'
const functions = getFunctions(app)

// Connect to Auth emulator in development
const isDev = import.meta.env.DEV
const useEmulators = import.meta.env.VITE_USE_EMULATORS
const shouldUseEmulators = isDev && useEmulators !== 'false'

debug.info('🔧 Firebase Auth Configuration:', {
  isDev,
  useEmulators,
  shouldUseEmulators,
  authDomain: firebaseConfig.authDomain,
  projectId: firebaseConfig.projectId,
})

if (shouldUseEmulators) {
  const authEmulatorUrl = 'http://localhost:9099'
  const functionsEmulatorUrl = 'localhost'
  const functionsEmulatorPort = 5001

  debug.info('🔧 Connecting to Firebase Auth Emulator:', authEmulatorUrl)
  debug.info(
    '🔧 Connecting to Firebase Functions Emulator:',
    `${functionsEmulatorUrl}:${functionsEmulatorPort}`,
  )

  // Only connect if not already connected
  if (!auth.emulatorConfig) {
    try {
      connectAuthEmulator(auth, authEmulatorUrl)
      debug.info('✅ Successfully connected to Auth Emulator')
    } catch (error) {
      debug.error('❌ Failed to connect to Auth Emulator:', error)
    }
  } else {
    debug.info('ℹ️ Auth Emulator already connected')
  }

  // Connect Functions emulator
  try {
    connectFunctionsEmulator(functions, functionsEmulatorUrl, functionsEmulatorPort)
    debug.info('✅ Successfully connected to Functions Emulator')
  } catch (error) {
    debug.error('❌ Failed to connect to Functions Emulator:', error)
  }
} else {
  debug.info('🌐 Using production Firebase services')
}

// Initialize Google Auth Provider
const googleProvider = new GoogleAuthProvider()

// Initialize Analytics (conditionally)
let analytics: ReturnType<typeof getAnalytics> | null = null
if (import.meta.env.PROD || import.meta.env.VITE_ENABLE_ANALYTICS_DEV === 'true') {
  isSupported().then((supported) => {
    if (supported) {
      analytics = getAnalytics(app)
    }
  })
}

// Initialize Firestore with persistent cache for offline support and reduced reads
import {
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  connectFirestoreEmulator,
} from 'firebase/firestore'

const db = initializeFirestore(app, {
  localCache: persistentLocalCache({
    tabManager: persistentMultipleTabManager(),
  }),
})

// Connect to Firestore emulator in development
if (shouldUseEmulators) {
  const firestoreEmulatorHost = 'localhost'
  const firestoreEmulatorPort = 8080

  debug.info(
    '🔧 Connecting to Firestore Emulator:',
    `${firestoreEmulatorHost}:${firestoreEmulatorPort}`,
  )

  try {
    connectFirestoreEmulator(db, firestoreEmulatorHost, firestoreEmulatorPort)
    debug.info('✅ Successfully connected to Firestore Emulator')
  } catch (error) {
    debug.error('❌ Failed to connect to Firestore Emulator:', error)
  }
}

// Export Firebase services
export { app, auth, db, functions, googleProvider, analytics }
export default app
